import React from 'react';
import {
  TextField,
  FormControl,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { AccessTime, Clear } from '@mui/icons-material';
import PropTypes from 'prop-types';

const TimePicker = ({
  value,
  onChange,
  label = 'Select Time',
  name,
  required = false,
  disabled = false,
  error = false,
  helperText = '',
  minTime,
  maxTime,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
  placeholder,
  showClearButton = false,
  clearButtonLabel = 'Clear time',
  step = 300, // 5 minute intervals by default
  format24Hour = true,
  startAdornment,
  endAdornment,
  showTimeIcon = true,
  ...otherProps
}) => {
  const handleChange = (event) => {
    const timeValue = event.target.value;
    if (onChange) {
      onChange(event, timeValue);
    }
  };

  const handleClear = () => {
    if (onChange && !disabled) {
      const syntheticEvent = {
        target: { name, value: '' },
      };
      onChange(syntheticEvent, '');
    }
  };

  // Generate default start adornment
  const getDefaultStartAdornment = () => {
    if (!showTimeIcon) return null;

    return (
      <InputAdornment position='start'>
        <AccessTime
          sx={{
            color: disabled ? 'rgba(0, 0, 0, 0.38)' : 'action.active',
            fontSize: size === 'small' ? '1rem' : '1.25rem',
          }}
        />
      </InputAdornment>
    );
  };

  // Generate default end adornment
  const getDefaultEndAdornment = () => {
    if (!showClearButton || !value || disabled) return null;

    return (
      <InputAdornment position='end'>
        <IconButton
          onClick={handleClear}
          edge='end'
          size={size}
          title={clearButtonLabel}
          sx={{
            visibility: value ? 'visible' : 'hidden',
          }}
        >
          <Clear fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </InputAdornment>
    );
  };

  // Determine final adornments
  const finalStartAdornment =
    startAdornment !== undefined ? startAdornment : getDefaultStartAdornment();
  const finalEndAdornment =
    endAdornment !== undefined ? endAdornment : getDefaultEndAdornment();

  // Format helper text with time format info
  const getFormattedHelperText = () => {
    if (error || helperText) {
      return helperText;
    }

    const formatInfo = format24Hour
      ? 'Format: 24-hour (HH:MM)'
      : 'Format: 12-hour (HH:MM AM/PM)';
    const minTimeInfo = minTime ? ` • Min: ${minTime}` : '';
    const maxTimeInfo = maxTime ? ` • Max: ${maxTime}` : '';

    return `${formatInfo}${minTimeInfo}${maxTimeInfo}`;
  };

  return (
    <FormControl fullWidth={fullWidth} error={error}>
      <TextField
        name={name}
        value={value || ''}
        onChange={handleChange}
        label={label}
        type='time'
        variant={variant}
        size={size}
        required={required}
        disabled={disabled}
        error={error}
        helperText={getFormattedHelperText()}
        fullWidth={fullWidth}
        placeholder={placeholder}
        InputLabelProps={{
          shrink: true,
          required: required,
        }}
        inputProps={{
          min: minTime,
          max: maxTime,
          step: step,
          ...otherProps.inputProps,
        }}
        InputProps={{
          startAdornment: finalStartAdornment,
          endAdornment: finalEndAdornment,
          ...otherProps.InputProps,
        }}
        sx={{
          '& input[type="time"]': {
            cursor: disabled ? 'not-allowed' : 'pointer',
            paddingLeft: showTimeIcon
              ? size === 'small'
                ? '32px'
                : '36px'
              : '8px',
            paddingRight: showClearButton && value ? '50px' : '40px',
            fontSize: '14px',
          },
          '& input[type="time"]:disabled': {
            cursor: 'not-allowed',
            color: 'rgba(0, 0, 0, 0.38)',
          },
          '& .MuiInputAdornment-positionStart': {
            position: 'absolute',
            left: '8px',
            zIndex: 1,
            pointerEvents: 'none',
          },
          '& input[type="time"]::-webkit-calendar-picker-indicator': {
            cursor: 'pointer',
            opacity: showTimeIcon ? 0 : 1, // Hide native picker when custom icon is shown
            filter: disabled ? 'grayscale(1)' : 'none',
            position: 'absolute',
            right: showClearButton && value ? '40px' : '8px',
            width: '20px',
            height: '20px',
            zIndex: 2, // Ensure it's above other elements
          },
          ...otherProps.sx,
        }}
        {...otherProps}
      />
    </FormControl>
  );
};

TimePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  minTime: PropTypes.string,
  maxTime: PropTypes.string,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  placeholder: PropTypes.string,
  showClearButton: PropTypes.bool,
  clearButtonLabel: PropTypes.string,
  step: PropTypes.number,
  format24Hour: PropTypes.bool,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  showTimeIcon: PropTypes.bool,
};

export default TimePicker;
